'use client';

import { useErrorBoundary } from '@/app/contexts/ErrorBoundaryContext';

import { useCallback } from 'react';

/**
 * Hook estable para manejo de errores que sincroniza con el contexto global. Previene múltiples overlays de error y proporciona funcionalidad de bloqueo.
 *
 * @returns {Object} Objeto con funciones para manejar errores, limpiar estado y verificar si las peticiones están bloqueadas.
 */
export const useStableErrorHandler = () => {
  const { showError, clearErrorState, errorState } = useErrorBoundary();

  const handleError = useCallback(
    (error: unknown, context?: string, retryAction?: () => void, goBackAction?: () => void) => {
      console.error(`Error in ${context || 'unknown context'}:`, error);

      if (!errorState.hasError) {
        showError(
          error instanceof Error ? error : new Error(String(error)),
          error instanceof Error ? error.message : 'Ha ocurrido un error inesperado',
          retryAction,
          goBackAction
        );
      }
    },
    [showError, errorState.hasError]
  );

  const clearError = useCallback(() => {
    clearErrorState();
  }, [clearErrorState]);

  const isBlocked = useCallback(() => {
    return errorState.hasError && errorState.isBlocking;
  }, [errorState.hasError, errorState.isBlocking]);

  return {
    handleError,
    clearError,
    isBlocked: isBlocked()
  };
};
